import mapboxgl from 'mapbox-gl'
import { simplifyCoordinatesForZoom } from './routeSimplification'

interface RouteLayerSet {
  strokeLayerId: string
  lineLayerId: string
  unpavedLayerId?: string
  unpavedOverlayLayerId?: string
  isLoaded: boolean
}

export class FlickerFreeRouteRenderer {
  private map: mapboxgl.Map
  private isDestroyed: boolean = false
  private isInitialized: boolean = false
  
  // Pre-loaded route layers - each route gets its own layers
  private routeLayers: Map<string, RouteLayerSet> = new Map()
  
  // Current state tracking
  private hoveredRouteId: string | null = null
  private selectedRouteId: string | null = null
  
  // Animation frame for smooth transitions
  private animationFrames: Map<string, number> = new Map()
  
  // Store route data for restoration after style changes
  private routeDataCache: Map<string, any> = new Map()
  
  // Store style change handler for proper cleanup
  private styleChangeHandler: (() => void) | null = null
  
  constructor(map: mapboxgl.Map) {
    this.map = map
    
    console.log(`🔧 FlickerFreeRouteRenderer constructor - map loaded: ${this.map.loaded()}`)
    
    // Initialize immediately if map is ready, otherwise wait
    if (this.map.loaded()) {
      console.log('📍 Map already loaded, initializing immediately')
      this.initialize()
    } else {
      console.log('⏳ Map not loaded yet, waiting for load event')
      this.map.once('load', () => {
        console.log('📍 Map load event fired, initializing now')
        this.initialize()
      })
      
      // Fallback: try again after a short delay in case we missed the load event
      setTimeout(() => {
        if (!this.isInitialized && this.map.loaded()) {
          console.log('🔄 Fallback initialization after timeout')
          this.initialize()
        }
      }, 1000)
    }
  }
  
  private initialize() {
    if (this.isInitialized || this.isDestroyed) {
      console.log(`⚠️ Initialize called but already initialized: ${this.isInitialized} or destroyed: ${this.isDestroyed}`)
      return
    }
    
    if (!this.map) {
      console.error('❌ Cannot initialize - map is null')
      return
    }
    
    try {
      console.log('🚀 Initializing FlickerFreeRouteRenderer')
      
      // Listen for style changes to restore routes
      this.styleChangeHandler = () => {
        console.log('🎨 Map style changed, restoring routes...')
        this.restoreRoutesAfterStyleChange()
      }
      this.map.on('style.load', this.styleChangeHandler)
      
      this.isInitialized = true
      console.log('✅ FlickerFreeRouteRenderer initialized successfully')
    } catch (error) {
      console.error('❌ Error during FlickerFreeRouteRenderer initialization:', error)
      this.isInitialized = false
    }
  }
  
  async renderRoute(route: any, mode: 'hover' | 'selected' = 'hover'): Promise<void> {
    console.log(`🎯 FlickerFree renderRoute called - destroyed: ${this.isDestroyed}, initialized: ${this.isInitialized}`)
    
    if (!this.map || this.isDestroyed) {
      console.warn('⚠️ FlickerFree: Map not available or destroyed')
      return
    }
    
    if (!this.isInitialized) {
      console.warn('⚠️ FlickerFree: Not initialized yet, attempting initialization...')
      
      // Try to initialize now if map is ready
      if (this.map.loaded()) {
        console.log('🔄 Map is loaded, trying immediate initialization')
        this.initialize()
        
        // If initialization succeeded, continue with rendering
        if (this.isInitialized) {
          console.log('✅ Immediate initialization successful, continuing render')
          // Continue to render below
        } else {
          console.error('❌ Immediate initialization failed')
          return
        }
      } else {
        console.log('⏳ Map not ready, deferring render')
        // Wait for map to be ready
        this.map.once('load', () => {
          console.log('🔄 FlickerFree: Map loaded, retrying render')
          this.renderRoute(route, mode)
        })
        return
      }
    }
    
    const routeId = route?.id || route?.firebaseRoute?.id
    if (!routeId) {
      console.warn('Route has no ID')
      return
    }
    
    // Check if already displayed in this mode
    if (mode === 'hover' && this.hoveredRouteId === routeId) {
      console.log(`🔄 Route ${routeId} already displayed in hover mode`)
      return
    }
    if (mode === 'selected' && this.selectedRouteId === routeId) {
      console.log(`🔄 Route ${routeId} already displayed in selected mode`)
      return
    }
    
    console.log(`🎨 FlickerFree: Rendering route ${routeId} in ${mode} mode`)
    
    // Hide previous route in this mode
    if (mode === 'hover' && this.hoveredRouteId) {
      await this.hideRoute(this.hoveredRouteId)
    }
    if (mode === 'selected' && this.selectedRouteId) {
      await this.hideRoute(this.selectedRouteId)
    }
    
    // Show the new route
    await this.showRoute(routeId, route, mode)
    
    // Update state
    if (mode === 'hover') {
      this.hoveredRouteId = routeId
    } else {
      this.selectedRouteId = routeId
    }
    
    // Cache the route data for restoration after style changes
    this.routeDataCache.set(routeId, { route, mode })
  }
  
  private async showRoute(routeId: string, route: any, mode: 'hover' | 'selected'): Promise<void> {
    let layerSet = this.routeLayers.get(routeId)
    
    // Create layers if they don't exist
    if (!layerSet) {
      layerSet = await this.createRouteLayer(routeId, route)
      if (!layerSet) return
    }
    
    // Determine target opacity based on mode
    const targetOpacity = mode === 'selected' ? 1 : 0.8
    
    // Smoothly fade in main route layers
    this.animateLayerOpacity(layerSet.strokeLayerId, 1, 150)
    this.animateLayerOpacity(layerSet.lineLayerId, targetOpacity, 150)
    
    // Smoothly fade in unpaved layers if they exist
    if (layerSet.unpavedLayerId) {
      this.animateLayerOpacity(layerSet.unpavedLayerId, targetOpacity, 150)
    }
    if (layerSet.unpavedOverlayLayerId) {
      this.animateLayerOpacity(layerSet.unpavedOverlayLayerId, 1, 150)
    }
  }
  
  private async hideRoute(routeId: string): Promise<void> {
    const layerSet = this.routeLayers.get(routeId)
    if (!layerSet) return
    
    // Smoothly fade out main route layers
    this.animateLayerOpacity(layerSet.strokeLayerId, 0, 200)
    this.animateLayerOpacity(layerSet.lineLayerId, 0, 200)
    
    // Smoothly fade out unpaved layers if they exist
    if (layerSet.unpavedLayerId) {
      this.animateLayerOpacity(layerSet.unpavedLayerId, 0, 200)
    }
    if (layerSet.unpavedOverlayLayerId) {
      this.animateLayerOpacity(layerSet.unpavedOverlayLayerId, 0, 200)
    }
  }
  
  private async createRouteLayer(routeId: string, route: any): Promise<RouteLayerSet | null> {
    try {
      console.log(`🔧 Creating dedicated layers for route ${routeId}`)
      
      // Generate unique layer IDs
      const strokeLayerId = `route-stroke-${routeId}`
      const lineLayerId = `route-line-${routeId}`
      const unpavedLayerId = `route-unpaved-${routeId}`
      const unpavedOverlayLayerId = `route-unpaved-overlay-${routeId}`
      
      // Prepare route data
      const routeData = this.prepareRouteData(route)
      if (!routeData || routeData.length === 0) {
        console.warn(`No route data for ${routeId}`)
        return null
      }
      
      // Create main route GeoJSON
      const mainFeatures = routeData.map((segment, index) => ({
        type: 'Feature' as const,
        properties: {
          color: segment.color,
          segmentIndex: index
        },
        geometry: {
          type: 'LineString' as const,
          coordinates: segment.coordinates
        }
      }))
      
      const mainGeojson = {
        type: 'FeatureCollection' as const,
        features: mainFeatures
      }
      
      // Create unpaved segments GeoJSON
      const unpavedFeatures: any[] = []
      routeData.forEach((segment, segmentIndex) => {
        if (segment.unpavedSegments && segment.unpavedSegments.length > 0) {
          segment.unpavedSegments.forEach((unpavedCoords, unpavedIndex) => {
            unpavedFeatures.push({
              type: 'Feature' as const,
              properties: {
                color: segment.color,
                segmentIndex: segmentIndex,
                unpavedIndex: unpavedIndex
              },
              geometry: {
                type: 'LineString' as const,
                coordinates: unpavedCoords
              }
            })
          })
        }
      })
      
      const unpavedGeojson = {
        type: 'FeatureCollection' as const,
        features: unpavedFeatures
      }
      
      // Add stroke layer (white outline)
      this.map.addSource(strokeLayerId, {
        type: 'geojson',
        data: mainGeojson
      })
      
      this.map.addLayer({
        id: strokeLayerId,
        type: 'line',
        source: strokeLayerId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ffffff',
          'line-width': 6,
          'line-opacity': 0 // Start invisible
        }
      })
      
      // Add main line layer
      this.map.addSource(lineLayerId, {
        type: 'geojson',
        data: mainGeojson
      })
      
      this.map.addLayer({
        id: lineLayerId,
        type: 'line',
        source: lineLayerId,
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': ['get', 'color'],
          'line-width': 4,
          'line-opacity': 0 // Start invisible
        }
      })
      
      // Add unpaved layer (dashed)
      if (unpavedFeatures.length > 0) {
        console.log(`🛤️ Adding ${unpavedFeatures.length} unpaved segments for route ${routeId}`)
        
        this.map.addSource(unpavedLayerId, {
          type: 'geojson',
          data: unpavedGeojson
        })
        
        this.map.addLayer({
          id: unpavedLayerId,
          type: 'line',
          source: unpavedLayerId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': ['get', 'color'],
            'line-width': 4,
            'line-opacity': 0, // Start invisible
            'line-dasharray': [0.5, 2] // Dashed pattern for unpaved
          }
        })
        
        // Add white dot overlay for unpaved segments
        this.map.addSource(unpavedOverlayLayerId, {
          type: 'geojson',
          data: unpavedGeojson
        })
        
        this.map.addLayer({
          id: unpavedOverlayLayerId,
          type: 'line',
          source: unpavedOverlayLayerId,
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#ffffff',
            'line-width': 2,
            'line-opacity': 0, // Start invisible
            'line-dasharray': [0.1, 2] // White dots pattern
          }
        })
      }
      
      const layerSet: RouteLayerSet = {
        strokeLayerId,
        lineLayerId,
        unpavedLayerId: unpavedFeatures.length > 0 ? unpavedLayerId : undefined,
        unpavedOverlayLayerId: unpavedFeatures.length > 0 ? unpavedOverlayLayerId : undefined,
        isLoaded: true
      }
      
      this.routeLayers.set(routeId, layerSet)
      console.log(`✅ Created layers for route ${routeId} (${unpavedFeatures.length} unpaved segments)`)
      
      return layerSet
      
    } catch (error) {
      console.error(`Error creating layers for route ${routeId}:`, error)
      return null
    }
  }
  
  private prepareRouteData(route: any) {
    const routeData: Array<{
      coordinates: [number, number][]
      color: string
      unpavedSegments?: [number, number][][]
    }> = []
    
    // Extract route segments
    let allRoutes: any[] = []
    
    if (route.routes && route.routes.length > 0) {
      allRoutes = route.routes
    } else if (route.coordinates && route.coordinates.length > 0) {
      allRoutes = [{
        coordinates: route.coordinates,
        color: route.color || '#10b981',  // Single routes should have color at route level
        unpavedSegments: route.unpavedSegments
      }]
    } else if (route.firebaseRoute?.routes && route.firebaseRoute.routes.length > 0) {
      allRoutes = route.firebaseRoute.routes
    } else if (route.firebaseRoute?.coordinates && route.firebaseRoute.coordinates.length > 0) {
      allRoutes = [{
        coordinates: route.firebaseRoute.coordinates,
        color: route.firebaseRoute.color || route.color || '#10b981',  // Check firebaseRoute.color first
        unpavedSegments: route.firebaseRoute.unpavedSegments
      }]
    }
    
    // Process each route segment
    allRoutes.forEach((segment) => {
      // Skip excluded segments (e.g., optional or train link segments)
      if (segment.excludeFromMaster) {
        return
      }
      
      if (!segment.coordinates || segment.coordinates.length === 0) return
      
      // Simplify coordinates based on zoom, preserving surface transitions if available
      const currentZoom = this.map.getZoom()
      const surfaces = segment.surfaces || segment.surfaceTypes
      const simplifiedCoordinates = simplifyCoordinatesForZoom(
        segment.coordinates, 
        currentZoom, 
        surfaces
      )
      
      routeData.push({
        coordinates: simplifiedCoordinates,
        color: segment.color || '#10b981',
        unpavedSegments: segment.unpavedSegments
      })
    })
    
    return routeData
  }
  
  private animateLayerOpacity(layerId: string, targetOpacity: number, duration: number = 200): void {
    console.log(`🎭 Animating ${layerId} opacity to ${targetOpacity} over ${duration}ms`)
    
    // Check if layer exists before trying to animate it
    if (!this.map.getLayer(layerId)) {
      console.log(`⚠️ Layer ${layerId} no longer exists, skipping animation`)
      return
    }
    
    // Cancel any existing animation for this layer
    const existingFrame = this.animationFrames.get(layerId)
    if (existingFrame) {
      cancelAnimationFrame(existingFrame)
    }
    
    const startTime = performance.now()
    const startOpacity = this.map.getPaintProperty(layerId, 'line-opacity') || 0
    console.log(`🎭 Starting opacity: ${startOpacity}, target: ${targetOpacity}`)
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Ease-out curve for natural motion
      const eased = 1 - Math.pow(1 - progress, 3)
      const opacity = startOpacity + (targetOpacity - startOpacity) * eased
      
      try {
        // Check if layer still exists before trying to animate it
        if (this.map.getLayer(layerId)) {
          this.map.setPaintProperty(layerId, 'line-opacity', opacity)
          if (progress === 1) {
            console.log(`✅ Animation complete for ${layerId}, final opacity: ${opacity}`)
          }
        } else {
          // Layer was removed, cancel animation
          console.log(`⚠️ Layer ${layerId} no longer exists, canceling animation`)
          this.animationFrames.delete(layerId)
          return
        }
      } catch (error) {
        console.error(`❌ Error setting opacity for ${layerId}:`, error)
        this.animationFrames.delete(layerId)
        return
      }
      
      if (progress < 1) {
        const frame = requestAnimationFrame(animate)
        this.animationFrames.set(layerId, frame)
      } else {
        this.animationFrames.delete(layerId)
      }
    }
    
    const frame = requestAnimationFrame(animate)
    this.animationFrames.set(layerId, frame)
  }
  
  clearHoveredRoute(): void {
    if (this.hoveredRouteId) {
      this.hideRoute(this.hoveredRouteId)
      this.routeDataCache.delete(this.hoveredRouteId)
      this.hoveredRouteId = null
    }
  }
  
  clearSelectedRoute(): void {
    if (this.selectedRouteId) {
      this.hideRoute(this.selectedRouteId)
      this.routeDataCache.delete(this.selectedRouteId)
      this.selectedRouteId = null
    }
  }
  
  clearAllRoutes(): void {
    this.clearHoveredRoute()
    this.clearSelectedRoute()
  }
  
  // Clear route layer references without removing layers (for when layers are cleaned up externally)
  clearRouteReferences(): void {
    console.log('🧹 Clearing route layer references from FlickerFreeRouteRenderer')
    
    // Cancel all animations
    this.animationFrames.forEach(frame => cancelAnimationFrame(frame))
    this.animationFrames.clear()
    
    // Clear route layer references without removing layers
    this.routeLayers.clear()
    this.hoveredRouteId = null
    this.selectedRouteId = null
  }

  private async restoreRoutesAfterStyleChange(): Promise<void> {
    if (this.isDestroyed || !this.map) return
    
    console.log('🔄 Restoring routes after style change...')
    
    // Clear the layer references as they no longer exist after style change
    this.routeLayers.clear()
    
    // Cancel any animations
    this.animationFrames.forEach(frame => cancelAnimationFrame(frame))
    this.animationFrames.clear()
    
    // Restore each cached route
    const routesToRestore = Array.from(this.routeDataCache.entries())
    
    for (const [routeId, { route, mode }] of routesToRestore) {
      console.log(`🔄 Restoring ${mode} route: ${routeId}`)
      
      try {
        // Re-create the route layers
        await this.showRoute(routeId, route, mode)
        
        // If this was the selected or hovered route, ensure state is correct
        if (mode === 'hover' && this.hoveredRouteId === routeId) {
          // Route is already visible with correct opacity
          console.log(`✅ Restored hovered route: ${routeId}`)
        } else if (mode === 'selected' && this.selectedRouteId === routeId) {
          // Route is already visible with correct opacity
          console.log(`✅ Restored selected route: ${routeId}`)
        }
      } catch (error) {
        console.error(`❌ Error restoring route ${routeId}:`, error)
        // Remove from cache if restoration failed
        this.routeDataCache.delete(routeId)
      }
    }
    
    console.log('✅ Route restoration complete')
  }

  destroy(): void {
    this.isDestroyed = true
    
    // Remove style change listener
    if (this.map && this.styleChangeHandler) {
      this.map.off('style.load', this.styleChangeHandler)
      this.styleChangeHandler = null
    }
    
    // Cancel all animations
    this.animationFrames.forEach(frame => cancelAnimationFrame(frame))
    this.animationFrames.clear()
    
    // Clear route data cache
    this.routeDataCache.clear()
    
    // Remove all route layers
    this.routeLayers.forEach((layerSet, routeId) => {
      try {
        // Remove main layers
        if (this.map.getLayer(layerSet.strokeLayerId)) {
          this.map.removeLayer(layerSet.strokeLayerId)
        }
        if (this.map.getLayer(layerSet.lineLayerId)) {
          this.map.removeLayer(layerSet.lineLayerId)
        }
        if (this.map.getSource(layerSet.strokeLayerId)) {
          this.map.removeSource(layerSet.strokeLayerId)
        }
        if (this.map.getSource(layerSet.lineLayerId)) {
          this.map.removeSource(layerSet.lineLayerId)
        }
        
        // Remove unpaved layers if they exist
        if (layerSet.unpavedLayerId) {
          if (this.map.getLayer(layerSet.unpavedLayerId)) {
            this.map.removeLayer(layerSet.unpavedLayerId)
          }
          if (this.map.getSource(layerSet.unpavedLayerId)) {
            this.map.removeSource(layerSet.unpavedLayerId)
          }
        }
        if (layerSet.unpavedOverlayLayerId) {
          if (this.map.getLayer(layerSet.unpavedOverlayLayerId)) {
            this.map.removeLayer(layerSet.unpavedOverlayLayerId)
          }
          if (this.map.getSource(layerSet.unpavedOverlayLayerId)) {
            this.map.removeSource(layerSet.unpavedOverlayLayerId)
          }
        }
      } catch (error) {
        console.warn(`Error removing layers for route ${routeId}:`, error)
      }
    })
    
    this.routeLayers.clear()
    this.map = null as any
  }
}